import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { DirectDomainMembersType } from "@/direct-restapi-types";
import { IndexedDB } from "@/indexeddb";
import { User } from "@/model";
import UserDao from "./user-dao";

type Users = { [domainId: string]: User[] };
type DomainMembers = { [domainId: string]: DirectDomainMembersType };

type PayloadAdd = {
  data: User[];
  domainId: string;
};

async function setIndexedDB(users: Users): Promise<void> {
  await Promise.all(
    Object.keys(users).map(async (key) => {
      await IndexedDB.set("USERS", key, users[key]);
    }),
  );
}

/** 組織内のユーザー一覧を取得する */
const getDomainUsers = (users: Users, me: User, domainId: string): User[] => {
  const result =
    (users[domainId] || [])
      .filter((u) => {
        if (u.id === me.id) return true; // 自分は許可

        // 組織内かどうかチェック
        const index = u.domainIdList.indexOf(domainId);
        return 0 <= index; // 組織外をフィルタリング
      })
      .map((u) => {
        // 自分ならそのまま返す
        if (u.id === me.id) return u;

        // 権限チェック（roleが取れ無い->members APIで取得できてない->権限外)
        const hasRole = u.getRoleId(domainId);
        if (hasRole) return u;
        return User.createNotFoundUser(u.id, u.directId);
      }) || [];
  return result;
};

export const useUserStore = defineStore("users", () => {
  const users = ref<Users>({});
  const _me = ref<User>(User.createNotFoundUser("dummy"));
  const domainMembers = ref<DomainMembers>({});

  /** ログインユーザー情報 */
  const me = computed<User>((): User => {
    // localStorageから消す用
    // TOOD: localStorageからusers.meを消して必要なくなったら消す
    if (!("isOwnerRole" in _me.value)) {
      return User.create(_me.value);
    }
    // `in` 演算子による絞り込みで private メンバ(s3ProfileIconUrl)を含む
    // 構造型へ変質するため、User クラスとして明示的に受け直す（型のみ・挙動不変）。
    return _me.value as User;
  });

  /** ユーザーIDを指定して取得する */
  const get = (cognitoUserId: string, domainId: string): User | undefined => {
    return (users.value[domainId] || []).find(
      (user) => user.id === cognitoUserId,
    );
  };

  /** 組織内のユーザー一覧を取得する */
  const getByDomainId = (domainId: string): User[] => {
    return getDomainUsers(users.value, me.value, domainId);
  };

  /** domain members API の結果を取得する */
  const getDomainMembers = (domainId: string): DirectDomainMembersType => {
    return domainMembers.value[domainId] || { contents: [] };
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const add = (payload: PayloadAdd): void => {
    users.value = { ...users.value, [payload.domainId]: payload.data };
  };

  // --- actions ---

  /**
   * DBからユーザー一覧を取得してstoreに保存
   * @param payload.data 取得するドメインID
   */
  const fetch = async (payload: {
    data: string | undefined;
  }): Promise<void> => {
    const domainId = payload.data || "";
    if (!domainId) {
      console.log("fetch user data must set domainid");
      return;
    }
    const users_by_api = await UserDao.read(domainId);
    // UserDao.read は User[] | undefined を返すが、既存挙動どおりそのまま格納する（型のみキャスト）。
    add({ data: users_by_api as User[], domainId });

    await setIndexedDB(users.value);
  };

  /** 自身の情報を取得する */
  const fetchMe = async (): Promise<boolean> => {
    try {
      const apiMe = await UserDao.getMe();
      if (apiMe) {
        const updated = Object.assign(User.create(me.value), {
          domainRoles: me.value.domainRoles,
          departments: me.value.departments,
          email: me.value.email,
        });
        updated.override(apiMe);
        _me.value = updated;
      }
      return !!apiMe;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  /** localStorageのusers/me情報をstoreへ反映させる用 */
  const setMe = async (payload: User): Promise<void> => {
    const next = Array.isArray(payload) ? payload[0] : payload;
    _me.value = next;
  };

  /** REST-APIの結果 domainMembersをセット */
  const setDomainMembers = async (payload: {
    domainId: string;
    domainMembers: DirectDomainMembersType;
  }): Promise<void> => {
    domainMembers.value = {
      ...domainMembers.value,
      [payload.domainId]: payload.domainMembers,
    };
  };

  const restore = async (): Promise<void> => {
    const restored = await IndexedDB.getAll("USERS");
    if (restored) {
      users.value = restored;
    }
  };

  return {
    users,
    _me,
    domainMembers,
    me,
    get,
    getByDomainId,
    getDomainMembers,
    fetch,
    fetchMe,
    setMe,
    setDomainMembers,
    restore,
  };
});

export default useUserStore;
